import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  Chip,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Store as StoreIcon,
  AccessTime,
  Block,
} from '@mui/icons-material';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Δευτέρα' },
  { value: 2, label: 'Τρίτη' },
  { value: 3, label: 'Τετάρτη' },
  { value: 4, label: 'Πέμπτη' },
  { value: 5, label: 'Παρασκευή' },
  { value: 6, label: 'Σάββατο' },
  { value: 0, label: 'Κυριακή' },
];

const StoreHoursSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [storeHours, setStoreHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    day_of_week: 1,
    open_time: '09:00',
    close_time: '22:00',
    is_closed: false,
  });

  useEffect(() => {
    fetchStoreHours();
  }, []);

  const fetchStoreHours = async () => {
    try {
      const response = await fetch('/api/store-settings/store-hours');
      if (response.ok) {
        const data = await response.json();
        setStoreHours(data);
      }
    } catch (err) {
      setError('Σφάλμα φόρτωσης ωραρίου');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const url = editingId
        ? `/api/store-settings/store-hours/${editingId}`
        : '/api/store-settings/store-hours';
      const method = editingId ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSuccess(editingId ? 'Ενημερώθηκε επιτυχώς' : 'Προστέθηκε επιτυχώς');
        setDialogOpen(false);
        fetchStoreHours();
        setFormData({ day_of_week: 1, open_time: '09:00', close_time: '22:00', is_closed: false });
        setEditingId(null);
      }
    } catch (err) {
      setError('Σφάλμα αποθήκευσης');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι;')) return;
    try {
      const response = await fetch(`/api/store-settings/store-hours/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Διαγράφηκε επιτυχώς');
        fetchStoreHours();
      }
    } catch (err) {
      setError('Σφάλμα διαγραφής');
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const getDayLabel = (day) => DAYS_OF_WEEK.find((d) => d.value === day)?.label || '';

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ color: '#008060' }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            background: 'linear-gradient(135deg, #008060 0%, #004c3f 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0, 128, 96, 0.3)',
          }}>
            <StoreIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Ωράριο Καταστήματος
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ορίστε τις ώρες λειτουργίας για κάθε ημέρα
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper sx={{
        borderRadius: 3, overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        {/* Action bar */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          p: { xs: 2, md: 3 }, borderBottom: '1px solid rgba(0,0,0,0.06)',
          bgcolor: 'rgba(0,0,0,0.01)',
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Ωράρια Λειτουργίας</Typography>
            <Typography variant="body2" color="text.secondary">
              {storeHours.length} {storeHours.length === 1 ? 'εγγραφή' : 'εγγραφές'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData({ day_of_week: 1, open_time: '09:00', close_time: '22:00', is_closed: false });
              setEditingId(null);
              setDialogOpen(true);
            }}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3,
              boxShadow: '0 4px 14px rgba(0,128,96,0.3)',
            }}
          >
            Προσθήκη
          </Button>
        </Box>

        {/* Table / Cards */}
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {storeHours.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Δεν υπάρχουν ωράρια. Προσθέστε ένα πατώντας "Προσθήκη".
            </Alert>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {storeHours.map((item) => (
                <Card key={item.id} variant="outlined" sx={{
                  borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                  transition: 'box-shadow 0.2s',
                }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {getDayLabel(item.day_of_week)}
                      </Typography>
                      {item.is_closed ? (
                        <Chip icon={<Block sx={{ fontSize: '14px !important' }} />} label="Κλειστό" size="small" color="error" variant="outlined" />
                      ) : (
                        <Chip
                          icon={<AccessTime sx={{ fontSize: '14px !important' }} />}
                          label={`${item.open_time} - ${item.close_time}`}
                          size="small"
                          sx={{ bgcolor: 'rgba(0,128,96,0.1)', color: '#008060', fontWeight: 600 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                      <IconButton
                        onClick={() => handleEdit(item)}
                        sx={{ width: 40, height: 40, borderRadius: '12px', border: '2px solid #64748b', color: '#64748b', '&:hover': { bgcolor: '#64748b', color: '#fff' } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(item.id)}
                        sx={{ width: 40, height: 40, borderRadius: '12px', border: '2px solid #ef4444', color: '#ef4444', '&:hover': { bgcolor: '#ef4444', color: '#fff' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ημέρα</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ώρα Ανοίγματος</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ώρα Κλεισίματος</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Κατάσταση</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ενέργειες</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storeHours.map((item) => (
                    <TableRow key={item.id} sx={{ '&:hover': { bgcolor: 'rgba(0,128,96,0.02)' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{getDayLabel(item.day_of_week)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={item.is_closed ? 'text.disabled' : 'text.primary'}>
                          {item.is_closed ? '-' : item.open_time}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={item.is_closed ? 'text.disabled' : 'text.primary'}>
                          {item.is_closed ? '-' : item.close_time}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.is_closed ? (
                          <Chip label="Κλειστό" size="small" color="error" variant="outlined" sx={{ fontWeight: 500 }} />
                        ) : (
                          <Chip label="Ανοιχτό" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(item)} sx={{ '&:hover': { color: '#008060' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingId ? 'Επεξεργασία Ωραρίου' : 'Νέο Ωράριο'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Ημέρα</InputLabel>
              <Select
                value={formData.day_of_week}
                label="Ημέρα"
                onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
              >
                {DAYS_OF_WEEK.map((day) => (
                  <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_closed}
                  onChange={(e) => setFormData({ ...formData, is_closed: e.target.checked })}
                  color="error"
                />
              }
              label="Κλειστό"
            />
            {!formData.is_closed && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Ώρα Ανοίγματος"
                  type="time"
                  value={formData.open_time}
                  onChange={(e) => setFormData({ ...formData, open_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Ώρα Κλεισίματος"
                  type="time"
                  value={formData.close_time}
                  onChange={(e) => setFormData({ ...formData, close_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2 }}>Ακύρωση</Button>
          <Button onClick={handleSave} variant="contained" sx={{ borderRadius: 2, fontWeight: 600 }}>Αποθήκευση</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert severity="success" sx={{ borderRadius: 2 }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Snackbar>
    </Container>
  );
};

export default StoreHoursSettings;
